import type { ChartDetail } from "../fit/detail.js";
import type { JsonObject } from "./types.js";
import { isObject } from "./json.js";

function degradeAxis(axis: unknown, detail: ChartDetail): unknown {
  if (!isObject(axis)) return axis;
  const next: JsonObject = { ...axis };
  if (!detail.axisTitles) delete next.title;
  if (!detail.tickLabels) {
    next.labels = false;
    next.ticks = false;
  }
  return next;
}

/** Applies `detail` to one scope (the spec or a group mark) and its groups. */
function degradeScope(scope: JsonObject, detail: ChartDetail): JsonObject {
  const next: JsonObject = { ...scope };
  if (Array.isArray(scope.axes)) {
    next.axes = scope.axes.map((axis: unknown) => degradeAxis(axis, detail));
  }
  if (!detail.legend) delete next.legends;
  if (Array.isArray(scope.marks)) {
    next.marks = scope.marks.map((mark: unknown) =>
      isObject(mark) && mark.type === "group"
        ? degradeScope(mark, detail)
        : mark,
    );
  }
  return next;
}

/**
 * Drops what a chart has no room for (see `chartDetailForSize`): axis titles,
 * then tick labels (and the chart's own title with them), then legends — in
 * every scope, so faceted and layered specs degrade too. Works on the Vega
 * spec, so it holds for Vega-Lite after compiling.
 */
export function degradeToDetail(
  spec: JsonObject,
  detail: ChartDetail,
): JsonObject {
  const next = degradeScope(spec, detail);
  if (!detail.tickLabels) delete next.title;
  return next;
}
